/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAlignment,
  selectBreakdown,
  selectDateComparison,
  selectSummaryAlignmentToggle,
  selectSymmetricComparisonToggle,
  selectIsDetailed,
  selectSummaryEndDate,
  selectSummaryBookingDate,
  selectSummaryStartDate,
  selectSummaryFilterList,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import {
  selectTargetDate,
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectEnableHotelGroups,
  selectReportType,
  selectHotelGroupDropdownList,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
} from 'modules/dashboard/selectors';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import { KEYS, REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import {
  getAlignmentAndDateComparisonSchema,
  getBookingDateSchema,
  getChartBreakdownFilterList,
  getComparisonStayDateSchema,
  getComparisonBookingDateSchema,
  getDashboardPmsSyncSchema,
  getNormalizedViewSchema,
  getPmsSyncSchema,
  getSymmetricComparisonSchema,
  getTargetDateSchema,
  getMaxStayDateSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import writeLog from 'modules/common/utils/filter-log';
import { ENVIRONMENT } from 'config';
import calculateComparisonDates, {
  hasLeapDay,
} from '../../../../../common/utils/dateComparisonUtils';
import buildSummaryHotelFilters from '../utils/build-summary-hotel-filters';

/**
 * Custom hook to build summary filters and update the leap comparison state
 * Dispatches filter changes when the summary report is active and the payload changes.
 */
const useSummaryVisualFilters = () => {
  const dispatch = useDispatch();
  const chartBreakdown = useSelector(selectBreakdown);
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const bookingDate = useSelector(selectSummaryBookingDate);
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const isDetailed = useSelector(selectIsDetailed);
  const endDate = useSelector(selectSummaryEndDate);
  const startDate = useSelector(selectSummaryStartDate);
  const alignmentToggle = useSelector(selectSummaryAlignmentToggle);
  const reportType = useSelector(selectReportType);
  const summaryFilterList = useSelector(selectSummaryFilterList);
  const hotelGroupsDropdownList = useSelector(selectHotelGroupDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  const handleLogging = (message) => {
    if (ENVIRONMENT.PBI_EMBED_DEBUG_MODE?.toLowerCase() === 'true') {
      console.log(message);
    }
  };
  // Triggered when pmsSyncToggle,chartBreakdown,bookingDate,visualFilters,dateComparison,alignment,targetDate,hotelId,symmetricComparisonToggle,isDetailed,endDate,startDate value changes
  useEffect(() => {
    if (
      !dateComparison ||
      !chartBreakdown ||
      !alignment ||
      !bookingDate ||
      !targetDate ||
      !visualFilters ||
      !hotelId ||
      (dateComparison && dateComparison === KEYS.CUSTOM && endDate === null)
    )
      return;
    // Generate filter list for summary widget top 8 visuals
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    const hotelFilters = buildSummaryHotelFilters({
      hotelId,
      hotelFocusOn,
      enableHotelGroups,
      enableSecondaryHotelGroups,
      isPrimaryHotelSetOverTime,
      invalidPrimaryHotels,
      isSecondaryHotelSetOverTime,
      invalidSecondaryHotels,
      visualFilters,
    });
    //
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const symmetricComparisonFilter = getSymmetricComparisonSchema(
      visualFilters,
      symmetricComparisonToggle
    );
    const chartBreakdownFilter = getChartBreakdownFilterList(
      visualFilters,
      chartBreakdown,
      isDetailed
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.SUMMARY);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const summaryPmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    const start = performance.now();
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    const end = performance.now();
    handleLogging(`${new Date().toISOString()} - Date calculation duration: ${end - start} ms`);
    //
    const hasLeap = hasLeapDay(adjustedStayDate?.startDate, adjustedStayDate?.endDate);
    dispatch(summaryActions.setIsSummaryLeapComparison(hasLeap));
    //
    if (
      chartBreakdownFilter &&
      bookingDateFilter &&
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      symmetricComparisonFilter &&
      normalizedFilter &&
      summaryPmsSyncSchema
    ) {
      const filters = [
        ...summaryPmsSyncSchema,
        ...pmsSyncFilter,
        chartBreakdownFilter,
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        symmetricComparisonFilter,
        ...hotelFilters,
        normalizedFilter,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      //
      if (
        JSON.stringify(filters) !== JSON.stringify(summaryFilterList) &&
        reportType === REPORT_TYPE.SUMMARY
      ) {
        //
        const dispatchTime = performance.now();
        handleLogging(
          `${new Date().toISOString()} - Dispatched updateSummaryFilterList after ${
            dispatchTime - start
          } ms`
        );
        writeLog('Summary Filter', filters);
        dispatch(summaryActions.updateSummaryFilterList(filters));
      }
    } else dispatch(summaryActions.updateSummaryFilterList([]));
  }, [
    pmsSyncToggle,
    chartBreakdown,
    bookingDate,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    symmetricComparisonToggle,
    isDetailed,
    endDate,
    startDate,
    enableHotelGroups,
    reportType,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    hotelGroupsDropdownList,
    normalizedViewToggle,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
    resetBookingDate,
  ]);
};

export default useSummaryVisualFilters;
